import React, { useEffect, useRef, useCallback } from 'react';
import PlaceholderDisplay from './PlaceholderDisplay';
import { useFloorScrolling } from '../../hooks/useFloorScrolling';
import { usePlaceholderDisplay } from '../../hooks/usePlaceholderDisplay';
import { FLOOR_LIST, LABELS } from '../../config';
import CallReadyDisplay from './CallReadyDisplay';

const FloorList = ({
  floors,
  selectedCurrentFloor,
  selectedDestinationFloor,
  currentSelectionMode,
  handicap,
  onFloorClick,
  onPlaceholderCancel,
  onSwapFloors,
  currentRegNumber
}) => {
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // CallReadyからの復帰時にスクロールするための情報を保持
  const scrollAfterTransition = useRef(null);

  // プレースホルダーの表示内容を更新する関数
  const handleFixedDisplay = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      setPlaceholderContents({ top: null, bottom: null });
      return;
    }

    const result = calculatePlaceholders(
      container,
      selectedCurrentFloor,
      selectedDestinationFloor
    );
    setPlaceholderContents(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCurrentFloor, selectedDestinationFloor]);

  // カスタムフックを使用
  const { floorRefs, setFloorRef } = useFloorScrolling(handleFixedDisplay);
  const { placeholderContents, setPlaceholderContents, calculatePlaceholders } =
    usePlaceholderDisplay(floorRefs);

  // Placeholderキャンセル時のハンドラー（スクロール処理を追加）
  const handlePlaceholderCancelWithScroll = useCallback((label, floor, placeholderId) => {
    // console.log('Placeholder cancel with scroll:', label, floor, 'from:', placeholderId);

    // 1. 親のキャンセル処理を実行（選択状態をクリア）
    if (onPlaceholderCancel) {
      onPlaceholderCancel(label, floor);
    }

    // 2. DOM更新後にスクロール（最下階スクロールと同じ遅延時間）
    setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;

      let targetFloor = floor;

      // bottom-placeholderの場合、1つ下の階を探す
      if (placeholderId === 'bottom-placeholder' && floors && floors.length > 0) {
        const currentIndex = floors.findIndex(f => f.floor === floor);
        if (currentIndex !== -1 && currentIndex < floors.length - 1) {
          targetFloor = floors[currentIndex + 1].floor;
          // console.log('Scrolling to next floor:', targetFloor);
        }
      }

      const targetElement = floorRefs.current[targetFloor];
      if (targetElement) {
        if (placeholderId === 'top-placeholder') {
          // topの場合：要素を一番上に表示
          targetElement.scrollIntoView({
            behavior: 'auto',
            block: 'start',
            inline: 'nearest'
          });
        } else {
          // bottomの場合：要素を一番下に表示（scrollTop計算で確実に）
          const elementTop = targetElement.offsetTop;
          const elementHeight = targetElement.offsetHeight;
          const containerHeight = container.clientHeight;

          // 要素の下端がコンテナの下端に来るように計算
          container.scrollTop = elementTop + elementHeight - containerHeight;
        }

        setTimeout(() => {
          handleFixedDisplay();
        }, 100);
      }
    }, 150);
  }, [onPlaceholderCancel, handleFixedDisplay, floors, floorRefs]);

  // FloorListの初期化・更新処理
  useEffect(() => {
    if (currentSelectionMode === 'call_ready' &&
      selectedCurrentFloor !== '-' &&
      selectedDestinationFloor !== '-') {
      return;
    }

    updateFloorListDisplay();

    setTimeout(() => {
      handleFixedDisplay();
    }, 50);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floors, selectedCurrentFloor, selectedDestinationFloor, currentSelectionMode, handicap, handleFixedDisplay, currentRegNumber]);

  // エレベータ変更時の初期スクロール処理（キャンセル処理と両立）
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !floors || floors.length <= 5) return;
    if (!currentRegNumber) return;

    // 両方未選択（初期表示）の場合のみ、最下階にスクロール
    if (selectedCurrentFloor === '-' && selectedDestinationFloor === '-') {
      setTimeout(() => {
        container.scrollTop = container.scrollHeight - container.clientHeight;
      }, 150);
    }
  }, [currentRegNumber, floors, selectedCurrentFloor, selectedDestinationFloor]);

  // CallReadyから階がクリックされた時の処理
  const handleFloorClickFromCallReady = useCallback((floor, isTopPosition) => {
    // console.log('Floor clicked from CallReady:', floor, 'isTop:', isTopPosition);

    scrollAfterTransition.current = {
      floor: floor,
      isTopPosition: isTopPosition
    };

    // let targetFloor = floor;

    // // bottom-placeholderの場合、1つ下の階を探す
    // if (!isTopPosition && floors && floors.length > 0) {
    //   const currentIndex = floors.findIndex(f => f.floor === floor);
    //   if (currentIndex !== -1 && currentIndex < floors.length - 1) {
    //     targetFloor = floors[currentIndex + 1].floor;
    //     // console.log('Scrolling to next floor:', targetFloor);
    //   }
    // }

    // const targetElement = floorRefs.current[targetFloor];
    // if (targetElement) {
    //   const block = isTopPosition ? 'start' : 'end';

    //   targetElement.scrollIntoView({
    //     behavior: 'auto',
    //     block: block,
    //     inline: 'nearest'
    //   });

    //   setTimeout(() => {
    //     handleFixedDisplay();
    //   }, 100);
    // }

    onFloorClick(floor);
  }, [onFloorClick]);

  // CallReadyからFloorListに戻った時のスクロール処理
  useEffect(() => {
    if (currentSelectionMode !== 'call_ready' && scrollAfterTransition.current) {
      const { floor, isTopPosition } = scrollAfterTransition.current;

      setTimeout(() => {
        const container = containerRef.current;
        if (!container) return;

        let targetFloor = floor;

        // bottomの場合の処理
        if (!isTopPosition && floors && floors.length > 0) {
          const currentIndex = floors.findIndex(f => f.floor === floor);
          // 最下階の場合は、初期スクロールと同じ処理（最下部までスクロール）
          if (currentIndex === 0) {
            // container.scrollTop = container.scrollHeight - container.clientHeight;
            const topPlaceHolder = document.getElementById('top-placeholder');
            topPlaceHolder.classList.add('hidden');
            floorRefs.current[floors[currentIndex].floor].scrollIntoView({ block: 'end' });
            scrollAfterTransition.current = null;
            // console.log(container, floorRefs.current[floors[currentIndex].floor]);
            topPlaceHolder.classList.remove('hidden');
            return;
          }
          // 最下階以外の場合は、1つ上の階を探す
          if (currentIndex !== -1 && currentIndex > 0 && currentIndex < floors.length - 1) {
            targetFloor = floors[currentIndex - 1].floor;
          }
        }

        const targetElement = floorRefs.current[targetFloor];
        if (container && targetElement) {
          if (isTopPosition) {
            // topの場合：要素を一番上に表示
            targetElement.scrollIntoView({
              behavior: 'auto',
              block: 'start',
              inline: 'nearest'
            });
          } else {
            // bottomの場合：要素を一番下に表示（scrollTop計算で確実に）
            const elementTop = targetElement.offsetTop;
            const elementHeight = targetElement.offsetHeight;
            const containerHeight = container.clientHeight;

            // 要素の下端がコンテナの下端に来るように計算
            container.scrollTop = elementTop + elementHeight - containerHeight;
          }

          // setTimeout(() => {
          //   handleFixedDisplay();
          // }, 100);
        }

        scrollAfterTransition.current = null;
      }, 100);
    }
  }, [currentSelectionMode, floors, handleFixedDisplay, floorRefs]);

  // スクロールイベントリスナーの設定
  useEffect(() => {
    if (currentSelectionMode === 'call_ready' &&
      selectedCurrentFloor !== '-' &&
      selectedDestinationFloor !== '-') {
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    let isTicking = false;
    const handleScroll = () => {
      if (!isTicking) {
        window.requestAnimationFrame(() => {
          handleFixedDisplay();
          isTicking = false;
        });
        isTicking = true;
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [currentSelectionMode, selectedCurrentFloor, selectedDestinationFloor, handleFixedDisplay]);

  // FloorListの表示を更新
  const updateFloorListDisplay = () => {

    const container = containerRef.current;
    const list = listRef.current;

    if (container && list) {
      list.className = 'mx-[calc(100vw/36)] text-center text-3xl font-light';

      const shouldCenter = floors && floors.length <= 5;
      if (shouldCenter) {
        container.className = 'w-full no-scrollbar';
      } else {
        container.className = 'w-full no-scrollbar ' + FLOOR_LIST.CONTAINER_HEIGHT + ' overflow-y-auto overflow-x-hidden';
      }
    }

    renderNormalFloorList();
  };

  // 通常のフロアリストをレンダリング
  const renderNormalFloorList = () => {
    const list = listRef.current;

    list.innerHTML = '';

    // エレベータ未選択時は5階分の「-」を表示
    if (!currentRegNumber || !floors) {
      for (let i = 0; i < 5; i++) {
        const li = document.createElement('li');
        li.className = 'flex justify-center items-center w-full py-[calc(100svh/144)] border-b border-gray-300 pointer-events-none';
        li.className += ` h-[${FLOOR_LIST.PLACEHOLDER_HEIGHT}]`;

        if (i === 0) {
          li.className += ' border-t-double';
        } else if (i === 4) {
          li.className += ' border-b-double';
        }

        const floorImageContainer = createFloorImageElement('-', 'CF');
        li.appendChild(floorImageContainer);
        list.appendChild(li);
      }
      return;
    }

    const sortedFloors = floors.slice().reverse();

    sortedFloors.forEach((floor, index) => {
      const li = document.createElement('li');
      li.dataset.floor = floor.floor;

      let classes = 'cursor-pointer active:bg-gray-100 flex justify-center items-center w-full py-[calc(100svh/144)]';
      classes += ` h-[${FLOOR_LIST.PLACEHOLDER_HEIGHT}]`;

      let borderClass = 'border-b border-gray-300';
      if (index === 0) {
        borderClass = 'border-t-double border-b border-gray-300';
      } else if (index === sortedFloors.length - 1) {
        borderClass = 'border-b-double border-gray-300';
      }

      const isCurrentFloor = floor.floor === selectedCurrentFloor;
      const isDestinationFloor = floor.floor === selectedDestinationFloor;
      const isSelected = isCurrentFloor || isDestinationFloor;
      const isFirstFloor = index === 0;
      const isLastFloor = index === sortedFloors.length - 1;

      if (isSelected) {
        classes = classes.replace('active:bg-gray-100', '');
        if (isCurrentFloor) {
          classes += ' ux-bg-selected-now rounded';
        } else {
          classes += ' ux-bg-selected-go rounded';
        }
        if (isFirstFloor) {
          classes += ' border-t-double';
        }
        if (isLastFloor) {
          classes += ' border-b-double';
        }
      } else {
        classes += ` ${borderClass}`;
        if (isFirstFloor) {
          classes += ' border-t-double';
        }
        if (isLastFloor) {
          classes += ' border-b-double';
        }
      }

      li.className = classes;

      if (isSelected) {
        const wrapper = document.createElement('div');
        wrapper.className = 'flex items-center justify-between w-full px-6 mx-1';

        const leftSpacer = document.createElement('div');
        leftSpacer.className = 'flex-shrink-0 opacity-0';
        const spacerLabel = document.createElement('span');
        spacerLabel.className = 'text-sm ux-text-lightgray font-bold scale-85';
        spacerLabel.textContent = isCurrentFloor ? LABELS.CURRENT : LABELS.DESTINATION;
        leftSpacer.appendChild(spacerLabel);

        const centerContainer = document.createElement('div');
        centerContainer.className = 'flex-1 flex justify-center';
        const floorImageContainer = createFloorImageElement(floor.frontName, 'CF-W');
        centerContainer.appendChild(floorImageContainer);

        const rightContainer = document.createElement('div');
        rightContainer.className = 'flex-shrink-0 flex justify-end';
        const label = document.createElement('span');
        label.className = 'text-sm ux-text-lightgray font-bold scale-85';
        label.textContent = isCurrentFloor ? LABELS.CURRENT : LABELS.DESTINATION;
        rightContainer.appendChild(label);

        wrapper.appendChild(leftSpacer);
        wrapper.appendChild(centerContainer);
        wrapper.appendChild(rightContainer);
        li.appendChild(wrapper);
      } else {
        const floorImageContainer = createFloorImageElement(floor.frontName, 'CF');
        li.appendChild(floorImageContainer);
      }

      li.addEventListener('click', () => onFloorClick(floor.floor));

      setFloorRef(floor.floor)(li);

      list.appendChild(li);
    });
  };

  // 階の画像要素を作成
  const createFloorImageElement = (floorText, prefix) => {
    const container = document.createElement('div');
    container.className = 'flex justify-center items-center space-x-1';

    for (let i = 0; i < floorText.length; i++) {
      const char = floorText[i];
      const img = document.createElement('img');
      img.src = `./assets/static/${prefix}__/${prefix}_${char}.png`;
      img.className = FLOOR_LIST.IMAGE_HEIGHT + ' w-auto scale-85';
      img.alt = char;

      img.onerror = function () {
        const span = document.createElement('span');
        span.textContent = char;
        span.className = 'text-3xl font-light text-gray-700';
        container.replaceChild(span, img);
      };

      container.appendChild(img);
    }

    return container;
  };

  // CallReadyモードの場合
  if (currentSelectionMode === 'call_ready' &&
    selectedCurrentFloor !== '-' &&
    selectedDestinationFloor !== '-') {
    return (
      <CallReadyDisplay
        floors={floors}
        selectedCurrentFloor={selectedCurrentFloor}
        selectedDestinationFloor={selectedDestinationFloor}
        handicap={handicap}
        onFloorClick={handleFloorClickFromCallReady}
        onSwapFloors={onSwapFloors}
      />
    );
  }

  // 通常のFloorListレンダリング
  return (
    <div className={FLOOR_LIST.CONTAINER_CLASS} style={{ height: FLOOR_LIST.CONTAINER_HEIGHT }}>
      <PlaceholderDisplay
        id="top-placeholder"
        className="mx-[calc(100vw/12)] rounded scale-85"
        content={placeholderContents.top}
        onCancel={(label, floor) => handlePlaceholderCancelWithScroll(label, floor, 'top-placeholder')}
        floors={floors}
      />

      <div
        ref={containerRef}
        id="floor-list-container"
      // style={{ height: FLOOR_LIST.CONTAINER_HEIGHT }}
      >
        <ul ref={listRef} className="text-center text-3xl font-light mx-[calc(100vw/36)]">
          {/* renderNormalFloorList()でコンテンツが動的に生成される */}
        </ul>
      </div>

      <PlaceholderDisplay
        id="bottom-placeholder"
        className="mx-[calc(100vw/12)] rounded scale-85"
        content={placeholderContents.bottom}
        onCancel={(label, floor) => handlePlaceholderCancelWithScroll(label, floor, 'bottom-placeholder')}
        floors={floors}
      />
    </div>
  );
};

export default FloorList;